import AddIcon from "@mui/icons-material/Add";
import { Button, Chip, Stack, TextField, Typography } from "@mui/material";
import { FC, KeyboardEvent, ReactNode, memo, useCallback, useEffect, useMemo, useState } from "react";
import { TVerificationAppFieldConfigItem } from "../../models";
import { FieldFormCheckboxRow } from "./FieldFormCheckboxRow";
import {
  FieldDefaultEditorKind,
  fieldSupportsStaticDefaultEditing,
  getFieldDefaultEditorKind,
} from "./helpers";

const DEFAULT_CHIP_SX = { alignSelf: "flex-start" } as const;

function getInitialDefaultValue(
  kind: Exclude<FieldDefaultEditorKind, "none" | "dynamic">,
): TVerificationAppFieldConfigItem["default"] {
  switch (kind) {
    case "string":
      return "";
    case "number":
      return 0;
    case "boolean":
    case "falseBoolean":
      return false;
    case "stringArray":
      return [];
    case "numberArray":
      return [];
  }
}

const ClearDefaultButton: FC<{ onClick: () => void }> = memo(({ onClick }) => (
  <Button size='small' onClick={onClick} sx={{ alignSelf: "flex-start" }}>
    Clear default
  </Button>
));

const DefaultArrayEditor: FC<{
  kind: "stringArray" | "numberArray";
  values: Array<string | number>;
  onChange: (next: string[] | number[]) => void;
  onClear: () => void;
}> = memo(({ kind, values, onChange, onClear }) => {
  const [draftValue, setDraftValue] = useState("");

  const handleAdd = useCallback(() => {
    const trimmedValue = draftValue.trim();

    if (!trimmedValue) {
      return;
    }

    if (kind === "numberArray") {
      const parsedValue = Number(trimmedValue);

      if (Number.isNaN(parsedValue)) {
        return;
      }

      onChange([...values, parsedValue] as number[]);
    } else {
      onChange([...values, trimmedValue] as string[]);
    }

    setDraftValue("");
  }, [draftValue, kind, onChange, values]);

  const handleDelete = useCallback(
    (index: number) => {
      onChange(values.filter((_, valueIndex) => valueIndex !== index) as string[] | number[]);
    },
    [onChange, values],
  );

  const handleDraftKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== "Enter") {
        return;
      }

      event.preventDefault();
      handleAdd();
    },
    [handleAdd],
  );

  return (
    <Stack spacing={1}>
      <Typography variant='caption' color='text.secondary'>
        Default values
      </Typography>
      <Stack direction='row' spacing={1} flexWrap='wrap' sx={{ gap: 1 }}>
        {values.map((value, index) => (
          <Chip
            key={`${String(value)}-${index}`}
            label={String(value)}
            size='small'
            onDelete={() => handleDelete(index)}
          />
        ))}
      </Stack>
      <Stack direction='row' spacing={1} alignItems='flex-start'>
        <TextField
          label='Add default value'
          value={draftValue}
          onChange={(event) => setDraftValue(event.target.value)}
          onKeyDown={handleDraftKeyDown}
          size='small'
          type={kind === "numberArray" ? "number" : undefined}
          fullWidth
        />
        <Button size='small' startIcon={<AddIcon />} onClick={handleAdd}>
          Add value
        </Button>
      </Stack>
      <ClearDefaultButton onClick={onClear} />
    </Stack>
  );
});

export const FieldDefaultEditor: FC<{
  extraAction?: ReactNode;
  field: TVerificationAppFieldConfigItem;
  onChange: (updated: TVerificationAppFieldConfigItem) => void;
}> = memo(({ extraAction, field, onChange }) => {
  const kind = getFieldDefaultEditorKind(field);
  const supportsStaticEditing = fieldSupportsStaticDefaultEditing(field.type);
  const [numberDraft, setNumberDraft] = useState("");

  useEffect(() => {
    setNumberDraft(typeof field.default === "number" ? String(field.default) : "");
  }, [field.default]);

  const updateDefault = useCallback(
    (value: TVerificationAppFieldConfigItem["default"]) => {
      onChange({ ...field, default: value });
    },
    [field, onChange],
  );

  const clearDefault = useCallback(() => {
    const { default: _removed, ...rest } = field;
    onChange(rest as TVerificationAppFieldConfigItem);
  }, [field, onChange]);

  const arrayValues = useMemo(
    () => Array.isArray(field.default) ? field.default : [],
    [field.default],
  );

  if (kind === "dynamic") {
    return (
      <Chip
        label='Default: dynamic — edit in Advanced tab'
        size='small'
        variant='outlined'
        color='info'
        sx={DEFAULT_CHIP_SX}
      />
    );
  }

  if (!supportsStaticEditing) {
    if (field.default === undefined) {
      return extraAction ? (
        <Stack direction='row' spacing={1} flexWrap='wrap' sx={{ gap: 1 }}>
          {extraAction}
        </Stack>
      ) : null;
    }

    return (
      <Chip
        label='Default preserved — edit in Advanced tab'
        size='small'
        variant='outlined'
        color='info'
        sx={DEFAULT_CHIP_SX}
      />
    );
  }

  if (kind === "none") {
    if (field.default === undefined) {
      return extraAction ? (
        <Stack direction='row' spacing={1} flexWrap='wrap' sx={{ gap: 1 }}>
          {extraAction}
        </Stack>
      ) : null;
    }

    return (
      <Chip
        label='Default preserved — edit in Advanced tab'
        size='small'
        variant='outlined'
        color='info'
        sx={DEFAULT_CHIP_SX}
      />
    );
  }

  if (field.default === undefined) {
    return (
      <Stack direction='row' spacing={1} flexWrap='wrap' sx={{ gap: 1 }}>
        <Button
          size='small'
          startIcon={<AddIcon />}
          onClick={() => updateDefault(getInitialDefaultValue(kind))}
          sx={{ alignSelf: "flex-start" }}
        >
          Add default
        </Button>
        {extraAction}
      </Stack>
    );
  }

  if (kind === "string") {
    return (
      <Stack spacing={1}>
        <TextField
          label='Default value'
          value={typeof field.default === "string" ? field.default : ""}
          onChange={(event) => updateDefault(event.target.value)}
          size='small'
          fullWidth
        />
        <ClearDefaultButton onClick={clearDefault} />
      </Stack>
    );
  }

  if (kind === "number") {
    return (
      <Stack spacing={1}>
        <TextField
          label='Default value'
          value={numberDraft}
          onChange={(event) => {
            const nextDraft = event.target.value;
            setNumberDraft(nextDraft);

            if (nextDraft.trim() === "") {
              return;
            }

            const parsedValue = Number(nextDraft);

            if (!Number.isNaN(parsedValue)) {
              updateDefault(parsedValue);
            }
          }}
          size='small'
          type='number'
          fullWidth
        />
        <ClearDefaultButton onClick={clearDefault} />
      </Stack>
    );
  }

  if (kind === "boolean") {
    return (
      <Stack spacing={0.5}>
        <FieldFormCheckboxRow
          label='Default value'
          checked={field.default === true}
          onChange={(checked) => updateDefault(checked)}
        />
        <ClearDefaultButton onClick={clearDefault} />
      </Stack>
    );
  }

  if (kind === "falseBoolean") {
    return (
      <Stack spacing={0.5}>
        <FieldFormCheckboxRow
          label='Default false'
          checked={field.default === false}
          onChange={(checked) => {
            if (checked) {
              updateDefault(false);
            } else {
              clearDefault();
            }
          }}
        />
        <ClearDefaultButton onClick={clearDefault} />
      </Stack>
    );
  }

  if (kind === "stringArray" || kind === "numberArray") {
    return (
      <DefaultArrayEditor
        kind={kind}
        values={arrayValues}
        onChange={updateDefault}
        onClear={clearDefault}
      />
    );
  }

  return null;
});
